<template>
    <div>
        <div class="selector-title font-weight-bold">{{ title }}</div>
        <div class="card-body mt-2 p-0">
            <div v-if="!variants" class="d-flex justify-content-center">
                <b-spinner variant="primary" label="Loading variants" />
            </div>

            <div v-if="variants && variants.length">
                <b-table
                    ref="variantsTable"
                    class="w-100 h-100 variants-table"
                    table-class="h-100"
                    striped
                    hover
                    :items="variants"
                    :fields="variantTableFields"
                    responsive="sm"
                    select-mode="multi"
                    :selectable="!hasDetailsView"
                    @row-selected="onRowSelected"
                >
                    <template #cell(name)="data">
                        <div
                            class="row-wrapper"
                            :class="getRowClasses(data)"
                            @click="
                                data.toggleDetails();
                                onDetailsVisibilityChange(data);
                            "
                        >
                            <div class="row-content">
                                <div v-if="data.item.image" class="d-inline">
                                    <b-img rounded thumbnail v-bind="thumbsProps" :src="data.item.image" />
                                </div>
                                <div class="ml-3 variant-name d-inline">{{ data.item.name }}</div>
                            </div>
                        </div>
                    </template>

                    <template #cell(price)="data">
                        <div
                            v-if="data.item.price"
                            class="align-items-center d-flex h-100 justify-content-end row-wrapper"
                            @click="
                                data.toggleDetails();
                                onDetailsVisibilityChange(data);
                            "
                        >
                            <div class="row-content">
                                <b>${{ data.item.price }}</b>
                            </div>
                        </div>
                    </template>

                    <template #row-details="{ item }">
                        <div class="row-wrapper">
                            <div class="row-content">
                                <slot name="row-details" :data="item" />
                            </div>
                        </div>
                    </template>
                </b-table>
            </div>

            <div v-if="variants && !variants.length">No variants available</div>
        </div>

        <button v-if="showDoneButton" class="btn btn-primary float-right" @click="onDone">Done</button>
    </div>
</template>

<script lang="ts" setup>
import { Ref, computed, onMounted, ref, useSlots, watch } from 'vue';
import { BTableRow } from '@/typings/bootstrap/table';
import { BTable } from 'bootstrap-vue';
import { GenericPodVariant } from './typings';

const props = withDefaults(
    defineProps<{
        value?: GenericPodVariant[];
        variants?: GenericPodVariant[];
        title?: string;
        showDoneButton?: boolean;
        rowClassSetter?: (row: BTableRow) => string[] | null;
    }>(),
    {
        value: () => [],
        variants: () => [],
        title: 'Select one or more product variants',
        showDoneButton: true
    }
);

const emit = defineEmits<{
    (event: 'input', value: GenericPodVariant[]): void;
    (event: 'selection-finished', value: GenericPodVariant[]): void;
    (event: 'details-visibility-change', value: BTableRow): void;
}>();

const slots = useSlots();

const thumbsProps = {
    width: 50,
    height: 50
};

const variantTableFields = [
    { key: 'name', label: '' },
    { key: 'price', label: '' }
];

const variantTableRef: Ref<BTable | null> = ref(null);

const hasDetailsView = computed((): boolean => {
    return !!slots['row-details'];
});

const selectedVariants = computed({
    get(): GenericPodVariant[] {
        return props.value;
    },
    set(value: GenericPodVariant[]) {
        emit('input', value);
    }
});

watch([selectedVariants, () => props.variants], () => {
    refreshSelectedRows();
});

onMounted(() => {
    refreshSelectedRows();
});

const getRowClasses = (row: BTableRow): { [key: string]: boolean } => {
    if (props.rowClassSetter) {
        const classes = props.rowClassSetter(row);
        if (classes) {
            const classesObject: { [key: string]: boolean } = {};
            for (const cssClass of classes) {
                classesObject[cssClass] = true;
            }
            return classesObject;
        }
    }
    return {};
};

const onRowSelected = (_selectedVariants: GenericPodVariant[]) => {
    selectedVariants.value = _selectedVariants;
};

const onDone = () => {
    emit('selection-finished', selectedVariants.value);
};

const onDetailsVisibilityChange = (row: BTableRow) => {
    row.detailsShowing = !row.detailsShowing;
    emit('details-visibility-change', row);
};

const refreshSelectedRows = () => {
    if (selectedVariants.value?.length && props.variants) {
        variantTableRef.value?.clearSelected();

        for (const variant of selectedVariants.value) {
            const index = props.variants.findIndex((v) => v.id === variant.id);
            if (index > -1) {
                variantTableRef.value?.selectRow(index);
            }
        }
    }
};
</script>

<style lang="scss" scoped>
:deep() .variants-table > table > tbody > tr {
    cursor: pointer;

    & > td {
        padding: 0;

        & > .row-wrapper .row-content {
            padding: 0.75rem;
        }

        & > .row-wrapper.table-active {
            background-color: #7258e0;
            border-color: #7258e0;
            color: white;
        }
    }
}

:deep() .variants-table td * {
    max-height: initial !important;
}

:deep() .variants-table > table > thead {
    display: none;
}
</style>
